import { createContext, use, type ReactNode } from 'react';

import type { StepUpAction, StepUpGrant } from '@/shared/http';

/** O pedido de uma autorização do step-up novo (`X-UY3-StepUp`) e os textos do painel que a pede. */
export interface StepUpAuthorizeRequest {
  readonly action: StepUpAction;
  /** O registro da rota protegida (`ResourceRouteParam`); ausente na criação. */
  readonly resourceId?: string | null | undefined;
  /** Título do painel; padrão "Confirme a operação". */
  readonly title?: string | undefined;
  /** A prévia do que vai ser confirmado (a Revisão), acima da senha. */
  readonly description?: ReactNode;
  /** Confirmar; padrão "Confirmar operação". */
  readonly submitLabel?: string | undefined;
  readonly destructive?: boolean | undefined;
  /**
   * Erro da operação que não é do step-up: `caller` (padrão) fecha o painel e rejeita, para a tela tratar (erro de
   * campo do formulário); `panel` mostra no aviso do painel, que continua aberto (a confirmação da ação).
   */
  readonly errors?: 'caller' | 'panel' | undefined;
}

/**
 * `withStepUp(action, resourceId, call)` do legado (`stepUpContext.tsx`): abre o painel de senha + código, chama
 * `call` com a autorização de uso único e resolve com o resultado. Fechar sem confirmar rejeita com erro `canceled`
 * (sem toast); erro da operação que não é do step-up fecha o painel e rejeita com ele, para a tela tratar.
 */
export type StepUpAuthorize = <T>(request: StepUpAuthorizeRequest, call: (grant: StepUpGrant) => Promise<T>) => Promise<T>;

const StepUpAuthorizeContext = createContext<StepUpAuthorize | null>(null);

/** Liga o painel do step-up novo à árvore; quem monta é o `StepUpProvider` de `shared/action`. */
export function StepUpAuthorizeProvider({ value, children }: { readonly value: StepUpAuthorize; readonly children: ReactNode }) {
  return <StepUpAuthorizeContext value={value}>{children}</StepUpAuthorizeContext>;
}

/** O `withStepUp` do legado na tela; exige o `StepUpProvider` (a casca autenticada monta). */
export function useStepUpAuthorize(): StepUpAuthorize {
  const authorize = use(StepUpAuthorizeContext);
  if (!authorize) throw new Error('StepUpProvider ausente: a casca autenticada precisa montá-lo');
  return authorize;
}

/** O mesmo, sem exigir o provider (o executor de ações, que também roda fora da casca). */
export function useOptionalStepUpAuthorize(): StepUpAuthorize | null {
  return use(StepUpAuthorizeContext);
}
